/**
 * WebSocket Context - Real-time updates for CamPassed
 * 
 * Provides organization-scoped WebSocket connection and event handling.
 * Automatically reconnects on disconnection with exponential backoff.
 * Falls back to polling mode when WebSocket is not available.
 */

import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { useAuth } from './AuthContext';

const WebSocketContext = createContext(null);

// Event types for type safety
export const WS_EVENTS = {
  // Guest events
  GUEST_CHECKED_IN: 'guest.checked_in',
  GUEST_APPROVED: 'guest.approved',
  GUEST_DENIED: 'guest.denied',
  
  // Event events
  EVENT_CREATED: 'event.created',
  EVENT_UPDATED: 'event.updated',
  EVENT_STATUS_CHANGED: 'event.status_changed',
  EVENT_CAPACITY_ALERT: 'event.capacity_alert',
  
  // Member events
  MEMBER_JOINED: 'member.joined',
  MEMBER_INVITED: 'member.invited',
  
  // Stats events
  STATS_UPDATED: 'stats.updated',
  
  // Scanner events
  SCANNER_VALID: 'scanner.valid_scan',
  SCANNER_INVALID: 'scanner.invalid_scan',
  SCANNER_ALREADY_USED: 'scanner.already_used',
  
  // Connection events
  CONNECTION_ESTABLISHED: 'connection.established',
};

export function WebSocketProvider({ children }) {
  const { session, organization } = useAuth();
  const [isConnected, setIsConnected] = useState(false);
  const [lastMessage, setLastMessage] = useState(null);
  const [connectionError, setConnectionError] = useState(null);
  const [mode, setMode] = useState('polling'); // Start in polling mode, upgrade to websocket if available
  
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttempts = useRef(0);
  const listenersRef = useRef(new Map());
  const pingIntervalRef = useRef(null);
  const pollingIntervalRef = useRef(null);
  
  const MAX_RECONNECT_ATTEMPTS = 1; // Only try once, then fall back to polling
  const BASE_RECONNECT_DELAY = 1000;
  const POLLING_INTERVAL = 30000; // Poll every 30 seconds
  
  // Get WebSocket URL
  const getWsUrl = useCallback(() => {
    if (!session?.access_token || !organization?.id) return null;
    
    const apiUrl = process.env.REACT_APP_BACKEND_URL || '';
    // Convert http(s) to ws(s)
    const wsProtocol = apiUrl.startsWith('https') ? 'wss' : 'ws';
    const wsHost = apiUrl.replace(/^https?:\/\//, '');
    
    return `${wsProtocol}://${wsHost}/api/ws/org/${organization.id}?token=${session.access_token}`;
  }, [session?.access_token, organization?.id]);
  
  // Subscribe to event type
  const subscribe = useCallback((eventType, callback) => {
    if (!listenersRef.current.has(eventType)) {
      listenersRef.current.set(eventType, new Set());
    }
    listenersRef.current.get(eventType).add(callback);
    
    // Return unsubscribe function
    return () => {
      const listeners = listenersRef.current.get(eventType);
      if (listeners) {
        listeners.delete(callback);
      }
    };
  }, []);
  
  // Dispatch event to listeners
  const dispatchEvent = useCallback((eventType, data) => {
    const listeners = listenersRef.current.get(eventType);
    if (listeners) {
      listeners.forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          console.error(`WebSocket listener error for ${eventType}:`, error);
        }
      });
    }
    
    // Also dispatch to wildcard listeners
    const wildcardListeners = listenersRef.current.get('*');
    if (wildcardListeners) {
      wildcardListeners.forEach(callback => {
        try {
          callback({ type: eventType, data });
        } catch (error) {
          console.error('WebSocket wildcard listener error:', error);
        }
      });
    }
  }, []);
  
  // Connect WebSocket
  const connect = useCallback(() => {
    const wsUrl = getWsUrl();
    if (!wsUrl) return;
    
    // Close existing connection
    if (wsRef.current) {
      wsRef.current.close();
    }
    
    try {
      const ws = new WebSocket(wsUrl);
      
      ws.onopen = () => {
        console.log('WebSocket connected - real-time updates enabled');
        setIsConnected(true);
        setConnectionError(null);
        setMode('websocket');
        reconnectAttempts.current = 0;
        
        // Clear any polling interval
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
        
        // Start ping interval
        pingIntervalRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: 'ping' }));
          }
        }, 30000);
      };
      
      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          
          // Skip pong messages
          if (message.type === 'pong') return;
          
          setLastMessage(message);
          dispatchEvent(message.type, message.data);
        } catch (error) {
          // Silent fail for message parsing
        }
      };
      
      ws.onerror = () => {
        // Silent - will be handled by onclose
      };
      
      ws.onclose = (event) => {
        setIsConnected(false);
        
        // Clear ping interval
        if (pingIntervalRef.current) {
          clearInterval(pingIntervalRef.current);
          pingIntervalRef.current = null;
        }
        
        // Handle different close scenarios
        if (event.code === 1000) {
          // Normal close, don't reconnect
          return;
        }
        
        // 403 or connection refused - platform doesn't support WebSocket
        // Fall back to polling mode immediately
        if (reconnectAttempts.current >= MAX_RECONNECT_ATTEMPTS) {
          console.log('Real-time WebSocket unavailable, using polling mode for updates');
          setMode('polling');
          setConnectionError(null); // Clear error since polling is working
          startPolling();
        } else {
          // Try to reconnect once
          reconnectAttempts.current++;
          reconnectTimeoutRef.current = setTimeout(connect, BASE_RECONNECT_DELAY);
        }
      };
      
      wsRef.current = ws;
    } catch (error) {
      // WebSocket not supported or other error
      console.log('WebSocket not available, using polling mode');
      setMode('polling');
      startPolling();
    }
  }, [getWsUrl, dispatchEvent]);
  
  // Start polling mode for updates
  const startPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }
    
    // In polling mode, we periodically dispatch a stats update event
    // Components can use this to refresh their data
    pollingIntervalRef.current = setInterval(() => {
      dispatchEvent(WS_EVENTS.STATS_UPDATED, { polled: true, timestamp: Date.now() });
    }, POLLING_INTERVAL);
    
    // Also dispatch immediately
    dispatchEvent(WS_EVENTS.STATS_UPDATED, { polled: true, timestamp: Date.now() });
  }, [dispatchEvent]);
  
  // Disconnect WebSocket
  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
    }
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }
    if (wsRef.current) {
      wsRef.current.close(1000, 'User disconnect');
      wsRef.current = null;
    }
    setIsConnected(false);
  }, []);
  
  // Connect when session and organization are available
  useEffect(() => {
    if (session?.access_token && organization?.id) {
      // Try WebSocket first
      connect();
      
      // Start polling as a backup (will be stopped if WebSocket connects)
      // This ensures we always have some form of updates
      const pollingTimer = setTimeout(() => {
        if (mode === 'polling' && !pollingIntervalRef.current) {
          startPolling();
        }
      }, 3000); // Give WebSocket 3 seconds to connect
      
      return () => {
        clearTimeout(pollingTimer);
        disconnect();
      };
    } else {
      disconnect();
    }
  }, [session?.access_token, organization?.id, connect, disconnect, mode, startPolling]);
  
  const value = {
    isConnected,
    lastMessage,
    connectionError,
    mode,
    subscribe,
    disconnect,
    reconnect: connect,
  };
  
  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
}

/**
 * Hook to use WebSocket context
 */
export function useWebSocket() {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocket must be used within a WebSocketProvider');
  }
  return context;
}

/**
 * Hook to subscribe to specific WebSocket events
 * 
 * @param {string} eventType - Event type to subscribe to
 * @param {function} callback - Callback function when event received
 * @param {array} deps - Dependencies array for callback memoization
 */
export function useWebSocketEvent(eventType, callback, deps = []) {
  const { subscribe } = useWebSocket();
  
  useEffect(() => {
    const unsubscribe = subscribe(eventType, callback);
    return unsubscribe;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventType, subscribe, ...deps]);
}

/**
 * Hook for real-time activity feed
 * Returns last N events of specified types
 */
export function useActivityFeed(eventTypes = [], maxItems = 10) {
  const [activities, setActivities] = useState([]);
  const { subscribe } = useWebSocket();
  
  useEffect(() => {
    const unsubscribers = eventTypes.map(eventType => 
      subscribe(eventType, (data) => {
        setActivities(prev => {
          const newActivity = {
            id: Date.now() + Math.random(),
            type: eventType,
            data,
            timestamp: data.timestamp || new Date().toISOString(),
          };
          return [newActivity, ...prev].slice(0, maxItems);
        });
      })
    );
    
    return () => unsubscribers.forEach(unsub => unsub());
  }, [eventTypes.join(','), maxItems, subscribe]);
  
  return activities;
}

export default WebSocketContext;
